import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import { useSEO } from 'hooks/useSEO';
import { useScrollTracking } from 'hooks/useScrollTracking';
import { useClickTracking } from 'hooks/useClickTracking';
import { OrganizationSchema } from 'components/JsonLd';

const Home = () => {
  useSEO({
    title: 'The Poetic Method | Healthcare Organizational Consulting',
    description: 'Strategic partnerships designed for institutional transformation. Integrating expressive arts, applied learning sciences, and evidence-based frameworks for healthcare organizations.',
  });

  useScrollTracking('home');
  useClickTracking('home');

  const services = [
    {
      title: 'Leadership Development',
      description: 'Cultivating leaders who can navigate complexity with both strategic acumen and emotional intelligence.',
      delay: 0.15
    },
    {
      title: 'Organizational Culture',
      description: 'Building environments where staff wellbeing and patient outcomes flourish together.',
      delay: 0.23
    },
    {
      title: 'Strategic Planning',
      description: 'Aligning organizational vision with practical pathways that honor your mission.',
      delay: 0.31
    }
  ];

  return (
    <>
      <OrganizationSchema />
      <div className="min-h-screen bg-cream">
        <Header />
      <main className="pt-20 sm:pt-24 md:pt-28">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-navy via-navy/90 to-teal px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden" aria-labelledby="hero-heading">
          <motion.div
            className="absolute top-[-50%] right-[-20%] w-[400px] sm:w-[500px] md:w-[600px] h-[400px] sm:h-[500px] md:h-[600px] rounded-full bg-teal/20 blur-3xl"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="max-w-3xl">
            <motion.p
              className="font-body font-medium text-xs sm:text-sm uppercase tracking-[2px] sm:tracking-[3px] text-teal/80 mb-4 sm:mb-5"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Organizational Consulting
            </motion.p>

            <motion.h1
              id="hero-heading"
              className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-5xl leading-tight text-cream mb-5 sm:mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              The Poetic Method™: Where Healthcare Insight Meets Poetic Wisdom
            </motion.h1>

            <motion.p
              className="font-body font-light text-base sm:text-lg md:text-xl leading-relaxed text-cream/85 mb-7 sm:mb-9"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Transforming organizations through a methodology that honors the science of improvement and the art of human connection.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 sm:gap-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <Link
                to="/contact"
                className="inline-block bg-coral text-white px-6 sm:px-8 py-3 sm:py-4 rounded-md font-body font-medium text-center text-sm sm:text-base transition-all duration-150 hover:bg-coral/90 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-teal/30 focus:ring-offset-2"
                aria-label="Navigate to contact page to start a conversation"
              >
                Start a Conversation
              </Link>
              <Link
                to="/about"
                className="inline-block bg-transparent text-cream px-6 sm:px-8 py-3 sm:py-4 border border-cream/30 rounded-md font-body font-medium text-center text-sm sm:text-base transition-all duration-150 hover:border-cream/60 hover:text-white active:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-cream/30 focus:ring-offset-2"
                aria-label="Learn more about our approach"
              >
                Our Approach
              </Link>
            </motion.div>
            </div>
          </div>
        </section>

        {/* TPM Philosophy Section */}
        <section className="bg-cream px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20" aria-labelledby="philosophy-heading">
          <div className="max-w-7xl mx-auto">
            {/** biome-ignore lint/correctness/useUniqueElementIds: <explanation> */}
            <motion.h2
              id="philosophy-heading"
              className="font-display text-2xl sm:text-3xl md:text-4xl text-navy mb-6 sm:mb-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Our Philosophy
            </motion.h2>

            <motion.div
              className="max-w-4xl mx-auto space-y-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <p className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed">
                The Poetic Method was born from a simple truth I've learned over and over again inside complex systems:
              </p>

              <p className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed font-semibold">
                People do not transform because they are managed.<br />
                They transform because they are met.
              </p>

              <p className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed">
                In healthcare, we often attempt to improve outcomes by tightening processes, adding dashboards, and increasing pressure. Those tools matter—but they are incomplete.
              </p>

              <p className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed">
                Sustainable excellence does not emerge from compliance alone. It emerges when human beings feel psychologically safe, emotionally seen, intellectually respected, and meaningfully connected to the work they are being asked to do.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 my-6 sm:my-8">
                <div className="bg-white p-4 rounded-lg border border-mist text-center">
                  <p className="font-body text-sm sm:text-base text-teal font-medium">Data reveals patterns</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-mist text-center">
                  <p className="font-body text-sm sm:text-base text-teal font-medium">Story reveals truth</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-mist text-center">
                  <p className="font-body text-sm sm:text-base text-teal font-medium">Structure stabilizes</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-mist text-center">
                  <p className="font-body text-sm sm:text-base text-teal font-medium">Creativity humanizes</p>
                </div>
              </div>

              {/* Two-Column Contrast */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 my-6 sm:my-8">
                <div className="bg-white p-4 sm:p-5 rounded-lg border border-mist">
                  <h3 className="font-display text-lg sm:text-xl text-teal mb-2">Improvement Science</h3>
                  <p className="font-body text-sm sm:text-base text-slate">
                    Data-driven analysis, process mapping, PDSA cycles, statistical controls, and measurable outcomes.
                  </p>
                </div>
                <div className="bg-white p-4 sm:p-5 rounded-lg border border-mist">
                  <h3 className="font-display text-lg sm:text-xl text-coral mb-2">Expressive Arts</h3>
                  <p className="font-body text-sm sm:text-base text-slate">
                    Poetry, reflective writing, creative facilitation, narrative medicine, and embodied learning.
                  </p>
                </div>
              </div>

              <h3 className="font-display text-lg sm:text-xl text-navy pt-2">This approach enables leaders to:</h3>

              <ul className="space-y-2 pl-4">
                <li className="flex items-start gap-3">
                  <span className="text-teal mt-1 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="font-body text-sm sm:text-base text-charcoal leading-relaxed">Surface what is actually happening beneath performance metrics</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal mt-1 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="font-body text-sm sm:text-base text-charcoal leading-relaxed">Rebuild trust in environments shaped by fatigue and constant change</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal mt-1 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="font-body text-sm sm:text-base text-charcoal leading-relaxed">Strengthen accountability without sacrificing compassion</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-teal mt-1 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="font-body text-sm sm:text-base text-charcoal leading-relaxed">Design systems that support human dignity while producing measurable results</span>
                </li>
              </ul>

              <div className="bg-navy/5 p-4 sm:p-5 rounded-lg border-l-4 border-navy my-4">
                <p className="font-body text-sm sm:text-base text-charcoal leading-relaxed">
                  At its core, The Poetic Method treats transformation as both a <strong>technical discipline</strong> (requiring rigor, measurement, and operational clarity) and a <strong>human practice</strong> (requiring presence, courage, emotional intelligence, and meaning-making).
                </p>
              </div>

              <p className="font-body text-base sm:text-lg text-charcoal leading-relaxed font-semibold">
                I don't believe organizations change first. I believe people do—and systems follow.
              </p>

              <p className="font-body text-base sm:text-lg text-charcoal leading-relaxed font-semibold">
                This is why my work does not begin with KPIs. It begins with listening.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="bg-cream px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20" aria-labelledby="services-heading">
          <div className="max-w-7xl mx-auto">
            <motion.div className="text-center max-w-4xl mx-auto mb-10 sm:mb-12 md:mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.6 }}>
              <h2 id="services-heading" className="font-display font-medium text-2xl sm:text-3xl md:text-4xl text-navy mb-3 sm:mb-4">How We Help</h2>
              <p className="font-body text-base sm:text-lg text-slate leading-relaxed px-4">Specialized consulting that addresses the unique challenges healthcare organizations face today.</p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {services?.map((service) => (
                <motion.article key={service.title} className="bg-white p-6 sm:p-8 md:p-10 rounded-lg border border-mist shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-1" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.5, delay: service?.delay }}>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal to-teal/70 rounded-xl mb-5 sm:mb-6" aria-hidden="true"></div>
                  <h3 className="font-display font-medium text-lg sm:text-xl md:text-2xl text-navy mb-2 sm:mb-3">{service?.title}</h3>
                  <p className="font-body text-sm sm:text-base text-slate leading-relaxed">{service?.description}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Scripture Section */}
        <section className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-gradient-to-br from-navy/95 via-navy/90 to-teal/85" aria-label="Scripture inspiration">
          <motion.figure
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <blockquote className="font-display text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed italic">
              "Commit your work to the Lord, and your plans will be established."
            </blockquote>
            <figcaption className="mt-6 sm:mt-8 font-body text-sm sm:text-base text-white/80">
              — Proverbs 16:3
            </figcaption>
          </motion.figure>
        </section>

        {/* CTA Section */}
        <section className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-cream" aria-labelledby="cta-heading">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              id="cta-heading"
              className="font-display text-2xl sm:text-3xl md:text-4xl text-navy mb-4 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to Transform?
            </motion.h2>

            <motion.p
              className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed mb-8 sm:mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Let's explore how The Poetic Method can help your organization achieve meaningful, measurable change.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-coral text-white px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-body font-semibold text-base sm:text-lg transition-all duration-300 hover:bg-coral/90 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
              >
                Start a Conversation
                <ArrowRight size={20} aria-hidden="true" />
              </Link>
            </motion.div>
          </div>
        </section>


      </main>
        <Footer />
      </div>
    </>
  );
};

export default Home;// Force rebuild Mon Feb 16 05:43:29 EST 2026
